import type {
  CompanyCustomField,
  ProposalCompanyResponse,
} from 'services/actions/ai_management_proposal_company';
import type { ProposalConditionsResponse } from 'services/actions/ai_management_proposal_conditions';
import type {
  ProposalItemColumnResponse,
  ProposalItemColumnType,
  ProposalItemResponse,
  ProposalItemsResponse,
  ProposalResponse,
  RuleResponse,
} from 'services/actions/ai_management_proposal';
import { valorPorExtenso } from './extenso';

// Estrutura sem formato: alimenta o DOCX, o PDF e a pré-visualização do arquivo.
export const VAZIO = '______';

export interface ProposalDocumentField {
  rotulo: string;
  valor: string;
}

export type ProposalDocumentAlign = 'esquerda' | 'direita' | 'centro';

export interface ProposalDocumentTable {
  colunas: string[];
  linhas: string[][];
  // Peso relativo da largura de cada coluna; DOCX e PDF distribuem a largura útil por esses pesos.
  pesos: number[];
  alinhamentos: ProposalDocumentAlign[];
}

export interface ProposalDocumentSection {
  titulo: string;
  campos?: ProposalDocumentField[];
  grupos?: Array<{ titulo: string; campos: ProposalDocumentField[] }>;
  tabela?: ProposalDocumentTable;
  paragrafos?: string[];
}

export interface ProposalDocumentSignature {
  local: string;
  data: string;
  nome: string;
  cpf: string;
  cargo: string;
}

export interface ProposalDocumentStructure {
  titulo: string;
  numeroLicitacao: string;
  secoes: ProposalDocumentSection[];
  assinatura: ProposalDocumentSignature;
}

export interface ProposalDocumentBidding {
  edital?: string | null;
  processo?: string | null;
  // biddings#show traz o órgão em public_body.nome; orgao_nome é reserva para licitação montada
  // por outra rota.
  public_body?: { nome?: string | null } | null;
  orgao_nome?: string | null;
  orgao_cidade?: string | null;
  orgao_estado?: string | null;
  objeto?: string | null;
  modality?: { nome?: string | null } | null;
}

export interface ProposalDocumentInput {
  bidding: ProposalDocumentBidding;
  proposal: ProposalResponse;
  items: Pick<ProposalItemsResponse, 'itens' | 'colunas' | 'casas_decimais' | 'julgamento'>;
  company: ProposalCompanyResponse | null;
  conditions: ProposalConditionsResponse | null;
  // Data de geração; injetada para a estrutura continuar pura.
  geradoEm: Date;
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const texto = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const ouVazio = (value: unknown) => texto(value) || VAZIO;

const digitos = (value: unknown) => texto(value).replace(/\D/g, '');

const mascaraCnpj = (value: unknown) => {
  const d = digitos(value);
  if (d.length !== 14) return texto(value);
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const mascaraCpf = (value: unknown) => {
  const d = digitos(value);
  if (d.length !== 11) return texto(value);
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

const moeda = (value: number, casas: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });

const numero = (value: unknown, casas?: number) => {
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return texto(value);
  return n.toLocaleString(
    'pt-BR',
    casas === undefined
      ? undefined
      : { minimumFractionDigits: casas, maximumFractionDigits: casas },
  );
};

// Data das colunas do cliente vem como "aaaa-mm-dd" (input date); sai dd/mm/aaaa.
const data = (value: unknown) => {
  const raw = texto(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : raw;
};

const dataPorExtenso = (d: Date) =>
  `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;

const formatarCelula = (value: unknown, tipo: ProposalItemColumnType, casas: number) => {
  if (value === null || value === undefined || texto(value) === '') return VAZIO;
  if (tipo === 'moeda') {
    const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    return Number.isFinite(n) ? moeda(n, casas) : texto(value);
  }
  if (tipo === 'numero') return numero(value);
  if (tipo === 'data') return data(value);
  return texto(value);
};

const quantidadeDe = (item: ProposalItemResponse) => {
  const n = Number(item.quantidade ?? 1);
  return Number.isFinite(n) ? n : 1;
};

// Mesmo cálculo do back (ProposalItems#item_total): preço do cliente × quantidade.
const totalDoItem = (item: ProposalItemResponse) =>
  typeof item.preco_unitario === 'number' ? item.preco_unitario * quantidadeDe(item) : null;

const valorDaColuna = (item: ProposalItemResponse, coluna: ProposalItemColumnResponse) => {
  switch (coluna.chave) {
    case 'numero_item':
      return item.numero_item;
    case 'descricao':
      return item.descricao;
    case 'unidade':
      return item.unidade;
    case 'quantidade':
      return item.quantidade;
    case 'marca':
      return item.marca;
    case 'modelo':
      return item.modelo;
    case 'fabricante':
      return item.fabricante;
    // Na proposta, valor unitário é o preço do cliente (não o estimado do edital).
    case 'valor_unitario':
      return item.preco_unitario;
    case 'valor_total':
      return totalDoItem(item);
    default:
      return item.valores ? item.valores[coluna.id] : null;
  }
};

const PESO_POR_CHAVE: Record<string, number> = {
  numero_item: 0.8,
  descricao: 4,
  unidade: 1,
  quantidade: 0.9,
  marca: 1.5,
  modelo: 1.5,
  fabricante: 1.5,
  valor_unitario: 1.6,
  valor_total: 1.7,
};

const pesoDa = (coluna: ProposalItemColumnResponse) =>
  (coluna.chave && PESO_POR_CHAVE[coluna.chave]) || 1.4;

const alinhamentoDa = (coluna: ProposalItemColumnResponse): ProposalDocumentAlign => {
  if (coluna.tipo === 'moeda' || coluna.tipo === 'numero') return 'direita';
  if (coluna.tipo === 'data' || coluna.chave === 'unidade') return 'centro';
  return 'esquerda';
};

const regraExige = (rules: RuleResponse[], nome: string) =>
  rules.some((r) => r.regra === nome && r.exige === true && r.status !== 'nao_se_aplica');

const camposPersonalizados = (campos: CompanyCustomField[] | undefined) =>
  [...(campos ?? [])]
    .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
    .map((c) => ({ rotulo: ouVazio(c.nome), valor: ouVazio(c.valor) }));

// Campos simples da empresa na ordem da etapa "Empresa e condições".
const EMPRESA_CAMPOS: Array<{ chave: string; rotulo: string }> = [
  { chave: 'razao_social', rotulo: 'Razão social' },
  { chave: 'cnpj', rotulo: 'CNPJ' },
  { chave: 'inscricao_estadual', rotulo: 'Inscrição estadual' },
  { chave: 'telefone', rotulo: 'Telefone' },
  { chave: 'endereco', rotulo: 'Endereço' },
  { chave: 'email', rotulo: 'E-mail' },
];

const REPRESENTANTE_CAMPOS = [
  { chave: 'nome', rotulo: 'Nome' },
  { chave: 'cpf', rotulo: 'CPF' },
  { chave: 'rg', rotulo: 'RG' },
  { chave: 'cargo', rotulo: 'Cargo' },
] as const;

const BANCO_CAMPOS = [
  { chave: 'banco', rotulo: 'Banco' },
  { chave: 'agencia', rotulo: 'Agência' },
  { chave: 'conta', rotulo: 'Conta' },
] as const;

const secaoEmpresa = (company: ProposalCompanyResponse | null): ProposalDocumentSection => {
  const dados = company?.dados ?? {};
  const rotulos = company?.rotulos ?? {};
  const excluidos = new Set(company?.excluidos ?? []);
  const rotulo = (chave: string, padrao: string) => texto(rotulos[chave]) || padrao;

  const campos = EMPRESA_CAMPOS.filter((c) => !excluidos.has(c.chave)).map((c) => ({
    rotulo: rotulo(c.chave, c.rotulo),
    valor: ouVazio(c.chave === 'cnpj' ? mascaraCnpj(dados[c.chave]) : dados[c.chave]),
  }));

  const representante = dados.representante_legal;
  const grupos: NonNullable<ProposalDocumentSection['grupos']> = [
    {
      titulo: 'Representante legal',
      campos: REPRESENTANTE_CAMPOS.map((c) => ({
        rotulo: c.rotulo,
        valor: ouVazio(
          c.chave === 'cpf' ? mascaraCpf(representante?.cpf) : representante?.[c.chave],
        ),
      })),
    },
  ];

  const banco = dados.dados_bancarios;
  const temBanco = BANCO_CAMPOS.some((c) => texto(banco?.[c.chave]));
  if (company?.dados_bancarios_exigidos || temBanco) {
    grupos.push({
      titulo: 'Dados bancários',
      campos: BANCO_CAMPOS.map((c) => ({ rotulo: c.rotulo, valor: ouVazio(banco?.[c.chave]) })),
    });
  }

  return {
    titulo: 'Dados da empresa',
    campos: [...campos, ...camposPersonalizados(company?.campos_personalizados)],
    grupos,
  };
};

const secaoLicitacao = (bidding: ProposalDocumentBidding): ProposalDocumentSection => {
  const cidade = [texto(bidding.orgao_cidade), texto(bidding.orgao_estado)]
    .filter(Boolean)
    .join('/');
  return {
    titulo: 'Dados da licitação',
    campos: [
      { rotulo: 'Edital', valor: ouVazio(bidding.edital) },
      { rotulo: 'Processo', valor: ouVazio(bidding.processo) },
      { rotulo: 'Órgão', valor: ouVazio(bidding.public_body?.nome || bidding.orgao_nome) },
      { rotulo: 'Cidade/UF', valor: ouVazio(cidade) },
      { rotulo: 'Objeto', valor: ouVazio(bidding.objeto) },
    ],
  };
};

const secaoItens = (
  items: ProposalDocumentInput['items'],
  casas: number,
): ProposalDocumentSection => {
  const selecionados = items.itens.filter((item) => item.selecionado);
  const colunas = [...items.colunas].filter((c) => !c.excluida).sort((a, b) => a.ordem - b.ordem);
  const comLote =
    items.julgamento === 'lote' || selecionados.some((item) => item.numero_lote !== null);

  const cabecalho = [...(comLote ? ['Lote'] : []), ...colunas.map((c) => c.nome)];
  const linhas = selecionados.map((item) => [
    ...(comLote ? [ouVazio(item.numero_lote)] : []),
    ...colunas.map((c) =>
      formatarCelula(valorDaColuna(item, c), c.tipo, c.tipo === 'moeda' ? casas : 0),
    ),
  ]);

  return {
    titulo: 'Itens',
    tabela: {
      colunas: cabecalho,
      linhas,
      pesos: [...(comLote ? [0.8] : []), ...colunas.map(pesoDa)],
      alinhamentos: [
        ...(comLote ? (['centro'] as ProposalDocumentAlign[]) : []),
        ...colunas.map(alinhamentoDa),
      ],
    },
  };
};

const secaoCondicoes = (conditions: ProposalConditionsResponse | null): ProposalDocumentSection => {
  const valores = conditions?.valores;
  const dias = (value: unknown) => (texto(value) ? `${texto(value)} dias` : VAZIO);
  return {
    titulo: 'Condições da proposta',
    campos: [
      { rotulo: 'Validade da proposta', valor: dias(valores?.validade_dias) },
      { rotulo: 'Prazo de entrega', valor: dias(valores?.prazo_entrega_dias) },
      { rotulo: 'Condições de pagamento', valor: ouVazio(valores?.condicoes_pagamento) },
      { rotulo: 'Garantia', valor: ouVazio(valores?.garantia) },
      ...camposPersonalizados(conditions?.campos_personalizados),
    ],
  };
};

// Mesma assinatura na proposta e em cada declaração.
export const buildSignature = (
  company: ProposalCompanyResponse | null,
  geradoEm: Date,
): ProposalDocumentSignature => {
  const representante = company?.dados?.representante_legal;
  return {
    // Nenhuma rota devolve a cidade da empresa: o local fica para o cliente preencher.
    local: VAZIO,
    data: dataPorExtenso(geradoEm),
    nome: ouVazio(representante?.nome),
    cpf: ouVazio(mascaraCpf(representante?.cpf)),
    cargo: ouVazio(representante?.cargo),
  };
};

export const buildProposalStructure = (input: ProposalDocumentInput): ProposalDocumentStructure => {
  const casas = input.items.casas_decimais ?? 2;
  const rules = input.proposal.rules ?? [];
  const total = input.items.itens
    .filter((item) => item.selecionado)
    .reduce((soma, item) => soma + (totalDoItem(item) ?? 0), 0);

  const totais: ProposalDocumentField[] = [{ rotulo: 'Valor total', valor: moeda(total, casas) }];
  if (regraExige(rules, 'valor_extenso')) {
    totais.push({ rotulo: 'Valor total por extenso', valor: valorPorExtenso(total) || VAZIO });
  }

  return {
    titulo: 'PROPOSTA COMERCIAL',
    numeroLicitacao: texto(input.bidding.edital),
    secoes: [
      secaoEmpresa(input.company),
      secaoLicitacao(input.bidding),
      secaoItens(input.items, casas),
      { titulo: 'Totais', campos: totais },
      secaoCondicoes(input.conditions),
    ],
    assinatura: buildSignature(input.company, input.geradoEm),
  };
};
